import React, { useCallback, useEffect, useRef, useState } from 'react'
import { formatFileSize, isExtensionAllowed, isSizeAllowed, withMinDuration } from './fileUpload.utils'
import { countFileRows } from './fileUpload.rows'

export type FileUploadStatus =
  | 'idle'
  | 'staging'
  | 'staged'
  | 'invalid'
  | 'uploading'
  | 'success'
  | 'error'

export interface UseFileUploadParams {
  accept: string[]
  onUpload: (file: File | null, files: File[]) => Promise<unknown>
  minAnimationMs?: number
  stagingMs?: number
  maxSizeMb?: number
  onSuccess?: () => void
  /** Spreadsheet row counting; turn off for non-tabular files such as images. */
  countRows?: boolean
  /** Allows submitting with no file — for flows carrying another payload. */
  allowFilelessUpload?: boolean
  /** Number of files the flow accepts. 1 keeps the original single-file behaviour. */
  maxFiles?: number
}

export interface UseFileUploadReturn {
  /** First file of the set — kept for single-file consumers. */
  file: File | null
  files: File[]
  status: FileUploadStatus
  rowCount: number | null
  stagingProgress: number
  errorMessage: string | null
  isDragActive: boolean
  canUpload: boolean
  selectFile: (file: File | null) => void
  /** Batch pick: the whole set is computed from one snapshot, unlike repeated selectFile calls. */
  selectFiles: (files: File[]) => void
  removeFile: (index: number) => void
  startUpload: () => void
  reset: () => void
  dragHandlers: {
    onDragOver: (e: React.DragEvent<HTMLElement>) => void
    onDragLeave: (e: React.DragEvent<HTMLElement>) => void
    onDrop: (e: React.DragEvent<HTMLElement>) => void
  }
}

const DEFAULT_MIN_ANIMATION_MS = 2000
const DEFAULT_STAGING_MS = 900
const DEFAULT_MAX_SIZE_MB = 10
const STAGING_TICK_MS = 100

interface UploadState {
  files: File[]
  status: FileUploadStatus
  rowCount: number | null
  stagingProgress: number
  errorMessage: string | null
}

const IDLE_STATE: UploadState = {
  files: [],
  status: 'idle',
  rowCount: null,
  stagingProgress: 0,
  errorMessage: null,
}

const useFileUpload = ({
  accept,
  onUpload,
  minAnimationMs = DEFAULT_MIN_ANIMATION_MS,
  stagingMs = DEFAULT_STAGING_MS,
  maxSizeMb = DEFAULT_MAX_SIZE_MB,
  onSuccess,
  countRows = true,
  allowFilelessUpload = false,
  maxFiles = 1,
}: UseFileUploadParams): UseFileUploadReturn => {
  const [state, setState] = useState<UploadState>(IDLE_STATE)
  const [isDragActive, setIsDragActive] = useState<boolean>(false)
  const isMountedRef = useRef<boolean>(true)
  // Generation counter: reset()/re-select invalidates pending staging timers and parses.
  const runIdRef = useRef<number>(0)
  const stagingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const clearStagingTimer = useCallback(() => {
    if (stagingTimerRef.current !== null) {
      clearInterval(stagingTimerRef.current)
      stagingTimerRef.current = null
    }
  }, [])

  useEffect(() => {
    isMountedRef.current = true
    return () => {
      isMountedRef.current = false
      clearStagingTimer()
    }
  }, [clearStagingTimer])

  const beginStaging = useCallback(
    (files: File[], errorMessage: string | null = null) => {
      const runId = ++runIdRef.current
      clearStagingTimer()
      setState({ files, status: 'staging', rowCount: null, stagingProgress: 0, errorMessage })

      let progressDone = false
      let rowsDone = false
      let rows: number | null = null

      const tryComplete = () => {
        if (!progressDone || !rowsDone) return
        if (runIdRef.current !== runId || !isMountedRef.current) return
        setState({ files, status: 'staged', rowCount: rows, stagingProgress: 100, errorMessage })
      }

      let elapsed = 0
      stagingTimerRef.current = setInterval(() => {
        if (runIdRef.current !== runId || !isMountedRef.current) {
          clearStagingTimer()
          return
        }
        elapsed += STAGING_TICK_MS
        if (elapsed >= stagingMs) {
          clearStagingTimer()
          progressDone = true
          setState((prev) => ({ ...prev, stagingProgress: 100 }))
          tryComplete()
        } else {
          const percent = Math.min(99, Math.round((elapsed / stagingMs) * 100))
          setState((prev) => ({ ...prev, stagingProgress: percent }))
        }
      }, STAGING_TICK_MS)

      // Non-tabular files (images) skip the workbook parse entirely.
      const rowsTask = countRows ? countFileRows(files[0]) : Promise.resolve<number | null>(null)
      rowsTask
        .then((count) => {
          rows = count
        })
        .catch(() => {
          rows = null
        })
        .then(() => {
          rowsDone = true
          tryComplete()
        })
    },
    [clearStagingTimer, stagingMs, countRows],
  )

  const performUpload = useCallback(
    async (files: File[]) => {
      setState((prev) => ({ ...prev, status: 'uploading', errorMessage: null }))
      try {
        await withMinDuration(onUpload(files[0] ?? null, files), minAnimationMs)
        if (!isMountedRef.current) return
        setState((prev) => ({ ...prev, status: 'success', errorMessage: null }))
        onSuccess?.()
      } catch (err) {
        if (!isMountedRef.current) return
        const message = err instanceof Error && err.message ? err.message : 'משהו השתבש, נסו שוב'
        setState((prev) => ({ ...prev, status: 'error', errorMessage: message }))
      }
    },
    [onUpload, minAnimationMs, onSuccess],
  )

  const isMulti = maxFiles > 1

  /**
   * In multi-file mode a bad pick must not wipe the set that is already staged —
   * it only reports the problem. The single-file flow keeps its original
   * behaviour, where the invalid file itself becomes the current one.
   */
  const rejectFile = useCallback(
    (file: File, errorMessage: string) => {
      if (isMulti) {
        setState((prev) => ({ ...prev, errorMessage }))
        return
      }
      runIdRef.current += 1
      clearStagingTimer()
      setState({
        files: [file],
        status: 'invalid',
        rowCount: null,
        stagingProgress: 0,
        errorMessage,
      })
    },
    [isMulti, clearStagingTimer],
  )

  const selectFile = useCallback(
    (file: File | null) => {
      if (!file) return
      if (!isExtensionAllowed(file.name, accept)) {
        rejectFile(file, 'פורמט הקובץ אינו נתמך')
        return
      }
      if (!isSizeAllowed(file.size, maxSizeMb)) {
        rejectFile(file, `הקובץ גדול מ-${maxSizeMb} MB (${formatFileSize(file.size)})`)
        return
      }
      if (isMulti && state.files.length >= maxFiles) {
        setState((prev) => ({ ...prev, errorMessage: `ניתן לצרף עד ${maxFiles} קבצים` }))
        return
      }
      beginStaging(isMulti ? [...state.files, file] : [file])
    },
    [accept, maxSizeMb, beginStaging, rejectFile, isMulti, maxFiles, state.files],
  )

  const selectFiles = useCallback(
    (incoming: File[]) => {
      if (incoming.length === 0) return
      // Single-file flow has no batch semantics: the first pick wins, exactly as selectFile.
      if (!isMulti) {
        selectFile(incoming[0])
        return
      }

      // The whole batch is judged against ONE snapshot of the set: calling
      // selectFile in a loop would read stale state and keep only the last file.
      const room = maxFiles - state.files.length
      const accepted: File[] = []
      let firstError: string | null = null
      const reject = (message: string) => {
        firstError = firstError ?? message
      }

      incoming.forEach((file) => {
        if (accepted.length >= room) {
          reject(`ניתן לצרף עד ${maxFiles} קבצים`)
          return
        }
        if (!isExtensionAllowed(file.name, accept)) {
          reject('פורמט הקובץ אינו נתמך')
          return
        }
        if (!isSizeAllowed(file.size, maxSizeMb)) {
          reject(`הקובץ גדול מ-${maxSizeMb} MB (${formatFileSize(file.size)})`)
          return
        }
        accepted.push(file)
      })

      // Nothing usable in the batch: report it, but keep the current set intact.
      if (accepted.length === 0) {
        if (firstError) setState((prev) => ({ ...prev, errorMessage: firstError }))
        return
      }
      beginStaging([...state.files, ...accepted], firstError)
    },
    [accept, maxSizeMb, beginStaging, selectFile, isMulti, maxFiles, state.files],
  )

  const removeFile = useCallback(
    (index: number) => {
      runIdRef.current += 1
      clearStagingTimer()
      setState((prev) => {
        const files = prev.files.filter((_, i) => i !== index)
        if (files.length === 0) return IDLE_STATE
        return { ...prev, files, status: 'staged', stagingProgress: 100, errorMessage: null }
      })
    },
    [clearStagingTimer],
  )

  const startUpload = useCallback(() => {
    const hasFiles = state.files.length > 0
    // A staged set is always preferred; the fileless branch only covers the
    // case where nothing was picked at all — an invalid pick still blocks.
    const canSendFiles = hasFiles && (state.status === 'staged' || state.status === 'error')
    const canSendFileless =
      allowFilelessUpload && !hasFiles && (state.status === 'idle' || state.status === 'error')
    if (!canSendFiles && !canSendFileless) return
    performUpload(state.files)
  }, [state.status, state.files, performUpload, allowFilelessUpload])

  const reset = useCallback(() => {
    runIdRef.current += 1
    clearStagingTimer()
    setState(IDLE_STATE)
    setIsDragActive(false)
  }, [clearStagingTimer])

  const onDragOver = useCallback((e: React.DragEvent<HTMLElement>) => {
    e.preventDefault()
    setIsDragActive(true)
  }, [])

  const onDragLeave = useCallback((e: React.DragEvent<HTMLElement>) => {
    e.preventDefault()
    setIsDragActive(false)
  }, [])

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLElement>) => {
      e.preventDefault()
      setIsDragActive(false)
      selectFile(e.dataTransfer.files[0] ?? null)
    },
    [selectFile],
  )

  return {
    file: state.files[0] ?? null,
    files: state.files,
    status: state.status,
    rowCount: state.rowCount,
    stagingProgress: state.stagingProgress,
    errorMessage: state.errorMessage,
    isDragActive,
    canUpload: state.status === 'staged' || (allowFilelessUpload && state.status === 'idle'),
    selectFile,
    selectFiles,
    removeFile,
    startUpload,
    reset,
    dragHandlers: { onDragOver, onDragLeave, onDrop },
  }
}

export default useFileUpload
